/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { SwitchButton, SwitchButtonProps } from '@euler/components/Switch';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { isNotNull } from '@euler/utils';
import React, { FC, memo, ReactElement, ReactNode, useCallback } from 'react';
import {
  StyleProp,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

export type SectionProps = {
  index?: number;
  title?: ReactNode;
  titlePlacement?: 'inside' | 'outside';
  style?: StyleProp<ViewStyle>;
  first?: boolean;
  last?: boolean;
  debugTag?: string;
  children?: ReactNode;
  titleStyle?: StyleProp<ViewStyle>;
  titleTextStyle?: StyleProp<TextStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  titleRight?: ReactNode;
  /**
   * Explanatory text under the section, outside the card — the counterpart
   * to `title`. Belongs to the section rather than to any one row, so it
   * stays legible without competing with the rows for weight.
   */
  tail?: ReactNode;
  tailStyle?: StyleProp<ViewStyle>;
  tailTextStyle?: StyleProp<TextStyle>;
};

export type Index = { section: number; row: number };

export type ItemProps = {
  tag?: string | null;
  disabled?: boolean;
  hidden?: boolean;
  disabledOpacity?: number;
  icon?: Exclude<ReactNode, string | number>;
  title?: ReactNode;
  subTitle?: ReactNode;
  note?: ReactNode;
  detail?: ReactNode;
  detailIcon?: 'disclosure' | 'info' | 'info-outline' | ReactElement;
  isSwitch?: boolean;
  switchProps?: Omit<SwitchButtonProps, 'on' | 'style' | 'onChange'>;
  switchOn?: boolean;
  switchDisabled?: boolean;
  index?: Index;
  first?: boolean;
  last?: boolean;
  style?: StyleProp<ViewStyle>;
  cellContainerStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  iconStyle?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<ViewStyle>;
  titleTextStyle?: StyleProp<TextStyle>;
  subTitleTextStyle?: StyleProp<TextStyle>;
  noteStyle?: StyleProp<TextStyle>;
  switchStyle?: StyleProp<ViewStyle>;
  separatorStyle?: StyleProp<ViewStyle>;
  detailTextStyle?: StyleProp<TextStyle>;
  children?: ReactNode;
  extra?: ReactNode;
  onPress?: (tag: string | null | undefined) => void;
  onLongPress?: (tag: string | null | undefined) => void;
  onSwitchChange?: (on: boolean, tag: string | null | undefined) => void;
};

const TableItem: FC<ItemProps> = memo(
  ({
    tag,
    disabled,
    disabledOpacity,
    hidden,
    icon,
    title,
    subTitle,
    note,
    detail,
    detailIcon,
    isSwitch,
    switchProps,
    switchOn,
    switchDisabled,
    children,
    extra,
    last,
    style,
    cellContainerStyle,
    contentContainerStyle,
    iconStyle,
    titleStyle,
    titleTextStyle,
    subTitleTextStyle,
    noteStyle,
    separatorStyle,
    switchStyle,
    detailTextStyle,
    onPress,
    onLongPress,
    onSwitchChange,
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const onItemPress = useCallback(() => {
      onPress?.(tag);
    }, [onPress, tag]);

    const onItemLongPress = useCallback(() => {
      onLongPress?.(tag);
    }, [onLongPress, tag]);

    const onItemSwitchChange = useCallback(
      (value: boolean) => {
        onSwitchChange?.(value, tag);
      },
      [onSwitchChange, tag],
    );

    if (hidden) return null;

    return (
      <View
        style={style}
        css={`
          flex-shrink: 0;
        `}
      >
        <View
          css={`
            position: absolute;
            background-color: ${theme.components.table.borderColor};
            height: ${last ? 0 : 1}px;
            left: 0;
            right: 0;
            bottom: 0;
          `}
          style={separatorStyle}
        />
        <TouchableOpacity
          onPress={onItemPress}
          onLongPress={onLongPress ? onItemLongPress : undefined}
          disabled={disabled}
          activeOpacity={onPress ? undefined : 1}
          css={`
            flex-grow: 1;
            /** important: without shrink set 0, the table
                view item will be  shrined when placing
                table view in scroll w/ dynamic contents
              */
            flex-shrink: 0;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            flex-wrap: nowrap;
            opacity: ${disabled ? disabledOpacity ?? 0.35 : 1};
            padding-left: ${rpx(15)}px;
            padding-right: ${rpx(15)}px;
            min-height: ${rpx(44)}px;
          `}
          style={cellContainerStyle}
        >
          {/* icon */}
          {icon ? (
            <View
              css={`
                flex-shrink: 0;
                flex-grow: 0;
              `}
              style={iconStyle}
            >
              {icon}
            </View>
          ) : null}
          {/* content */}
          <View
            css={`
              flex: 1;
              justify-content: center;
            `}
            style={contentContainerStyle}
          >
            <View
              css={`
                flex-direction: row;
                align-items: center;
                flex-wrap: nowrap;
              `}
              style={titleStyle}
            >
              {typeof title === 'string' || typeof title === 'number' ? (
                <Text
                  css={`
                    color: ${theme.components.table.item.titleColor};
                    font-family: ${FontFamily.NotoSans.Regular};
                    font-size: ${rpx(14)}px;
                    line-height: ${rpx(18)}px;
                  `}
                  style={titleTextStyle}
                >
                  {title}
                </Text>
              ) : (
                title
              )}
              {typeof subTitle === 'string' || typeof subTitle === 'number' ? (
                <Text
                  css={`
                    color: ${theme.components.table.item.subTitleColor};
                    font-family: ${FontFamily.NotoSans.Light};
                    font-size: ${rpx(12)}px;
                    line-height: ${rpx(18)}px;
                    margin-left: ${rpx(4)}px;
                  `}
                  style={subTitleTextStyle}
                >
                  {subTitle}
                </Text>
              ) : (
                subTitle
              )}
            </View>
            {note && (typeof note === 'string' || typeof note === 'number') ? (
              <Text
                css={`
                  color: ${theme.components.table.item.noteColor};
                  font-family: ${FontFamily.NotoSans.Light};
                  font-size: ${rpx(14)}px;
                  line-height: ${rpx(16)}px;
                `}
                style={noteStyle}
              >
                {note}
              </Text>
            ) : (
              <>{note}</>
            )}
            {children}
          </View>
          {/* detail */}
          {(detail != null || isSwitch || detailIcon != null) && (
            <View
              css={`
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                flex-wrap: nowrap;
                /* The pull-right compensates for the whitespace baked into
                   the detail icon glyph, so it applies only when there is
                   one. Without an icon it just ate the container's right
                   padding and left the detail text against the card edge. */
                margin-right: ${detailIcon != null ? rpx(-10) : 0}px;
              `}
            >
              {isSwitch ? (
                <SwitchButton
                  on={switchOn}
                  disabled={switchDisabled}
                  onChange={onItemSwitchChange}
                  style={switchStyle}
                  {...switchProps}
                />
              ) : null}
              {detail &&
              (typeof detail === 'string' || typeof detail === 'number') ? (
                <Text
                  css={`
                    color: ${theme.components.table.item.detailColor};
                    font-family: ${FontFamily.NotoSans.Light};
                    font-size: ${rpx(14)}px;
                    line-height: ${rpx(16)}px;
                  `}
                  style={detailTextStyle}
                >
                  {detail}
                </Text>
              ) : (
                <>{detail}</>
              )}
              {detailIcon === 'disclosure' ? (
                <Icon
                  name="chevron-right"
                  size={rpx(24)}
                  color={theme.components.table.item.detailDisclosureIconColor}
                />
              ) : detailIcon === 'info' ? (
                <Icon
                  name="information-solid"
                  size={rpx(24)}
                  color={theme.components.table.item.detailInfoIconColor}
                />
              ) : detailIcon === 'info-outline' ? (
                <Icon
                  name="information-outline"
                  size={rpx(24)}
                  color={theme.components.table.item.detailInfoIconColor}
                />
              ) : (
                detailIcon
              )}
            </View>
          )}
        </TouchableOpacity>
        {extra}
      </View>
    );
  },
);

TableItem.displayName = 'TableItem';

const TableSection: FC<SectionProps> = memo(
  ({
    title,
    titlePlacement,
    style,
    index,
    titleStyle,
    titleTextStyle,
    contentStyle,
    titleRight,
    tail,
    tailStyle,
    tailTextStyle,
    ...properties
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const children = React.Children.toArray(properties.children).filter(
      isNotNull,
    );
    const count = children.length;
    const childNodes = children.map((elem, idx) => {
      if (React.isValidElement(elem)) {
        const props = elem.props as ItemProps;
        const overrides: ItemProps = {
          index: {
            section: index ?? 0,
            row: idx,
          },
        };
        if (!('first' in props) && idx === 0) {
          overrides.first = true;
        }
        if (!('last' in props) && idx === count - 1) {
          overrides.last = true;
        }
        return React.cloneElement(elem, overrides);
      }
    });

    const renderTitle = () => {
      return (
        <HStack style={titleStyle}>
          {typeof title === 'string' || typeof title === 'number' ? (
            <Text
              css={`
                color: ${theme.components.table.section.titleColor};
                font-family: ${FontFamily.NotoSans.Regular};
                font-size: ${rpx(14)}px;
                line-height: ${rpx(16)}px;
              `}
              style={titleTextStyle}
            >
              {title}
            </Text>
          ) : (
            title
          )}
          {titleRight}
        </HStack>
      );
    };

    const renderTail = () => {
      if (typeof tail !== 'string' && typeof tail !== 'number') {
        return tail;
      }
      return (
        <Text
          css={`
            color: ${theme.components.table.section.tailColor};
            font-family: ${FontFamily.NotoSans.Light};
            font-size: ${rpx(12)}px;
            line-height: ${rpx(17)}px;
          `}
          style={tailTextStyle}
        >
          {tail}
        </Text>
      );
    };

    return (
      <>
        {title != null && titlePlacement === 'outside' && renderTitle()}
        <View style={style}>
          {title != null && titlePlacement !== 'outside' ? renderTitle() : null}
          <View style={contentStyle}>{childNodes}</View>
          {/* Inside the section box on purpose: the section's own bottom
              margin then spaces card-plus-tail from whatever follows, so a
              section reads the same distance from the next one whether or
              not it has a tail. */}
          {tail != null && <View style={tailStyle}>{renderTail()}</View>}
        </View>
      </>
    );
  },
);

TableSection.displayName = 'TableSection';

export type TableViewProps = {
  style?: StyleProp<ViewStyle>;
  children?: ReactNode;
  debugTag?: string;
};

export const TableView: FC<TableViewProps> & {
  Section: FC<SectionProps>;
  Item: FC<ItemProps>;
} = memo((tableProps: TableViewProps) => {
  const { style, children } = tableProps;
  let lastSectionIndex = -1;
  let currentSectionIndex = -1;
  let rowIndexInNextSection = 0;
  const childArray = React.Children.toArray(children).filter(isNotNull);
  const count = childArray.length;
  const childNodes = childArray.map((elem, index) => {
    if (React.isValidElement(elem)) {
      const displayName = findDisplayName(elem);
      const isSection = Boolean(
        elem.type === TableSection ||
          displayName?.includes('TableSection') ||
          displayName?.includes('TableViewSection') ||
          (elem.type as any)?.type?.toString()?.includes('TableSection'),
      );

      const isItem = Boolean(
        elem.type === TableItem ||
          displayName?.includes('TableItem') ||
          displayName?.includes('TableViewItem') ||
          (elem.type as any)?.type?.toString()?.includes('TableItem'),
      );

      if (!isSection && !isItem && __DEV__) {
        console.warn('TableView can only contain section or item as child');
      }
      if (isSection) {
        lastSectionIndex = index;
        currentSectionIndex = -1;
        rowIndexInNextSection = 0;
        const props = elem.props as SectionProps;
        const overrides: SectionProps = { index };
        if (!('first' in props) && index === 0) {
          overrides.first = true;
        }
        if (!('last' in props) && index === count - 1) {
          overrides.last = true;
        }
        if (Object.keys(overrides).length) {
          return React.cloneElement(elem, overrides);
        }
      } else {
        if (currentSectionIndex === -1) {
          ++lastSectionIndex;
          currentSectionIndex = lastSectionIndex;
        }
        const props = elem.props as ItemProps;
        const overrides: ItemProps = {
          index: {
            section: currentSectionIndex,
            row: rowIndexInNextSection++,
          },
        };
        if (!('first' in props) && index === 0) {
          overrides.first = true;
        }
        if (!('last' in props) && index === count - 1) {
          overrides.last = true;
        }
        return React.cloneElement(elem, overrides);
      }
    }
  });
  return <View style={style}>{childNodes}</View>;
}) as any;

TableView.Section = TableSection;
TableView.Item = TableItem;

function findDisplayName(elem: any): string | undefined {
  // if elem.type contains property `styledComponentId` and `target`
  // then this element is a styled component element and try to find the
  // display name of the nested target.
  if (!elem.type) return '';
  if (elem.type.styledComponentId && elem.type.target) {
    return findDisplayName(elem.type.target);
  }
  return elem.displayName ?? elem.type.displayName ?? elem.type.name;
}
